/**
 * Nordleaf Clinics - Wizard State Management
 * Handles the step-by-step booking flow
 */

class NordleafClinicsWizard {
  constructor() {
    this.currentStep = 1;
    this.selectedType = null;
    this.selectedMode = null;
    this.selectedService = null;
    
    this.steps = {
      hero: document.querySelector('[data-clinics-step="hero"]'),
      typeSelector: document.querySelector('[data-clinics-step="type-selector"]'),
      modeSelector: document.querySelector('[data-clinics-step="mode-selector"]')
    };

    this.init();
  }

  init() {
    if (!this.steps.typeSelector) {
      return;
    }
    this.#attachEventListeners();
    this.#loadStateFromStorage();
    this.#showCurrentStep();
  }

  #attachEventListeners() {
    // Type selection (Step 1)
    const typeButtons = document.querySelectorAll('[data-clinics-action="select-type"]');
    
    for (const button of typeButtons) {
      button.addEventListener('click', (e) => this.#handleTypeSelection(e));
    }

    // Mode selection (Step 2) - Use event delegation on the container
    const modeContainer = document.querySelector('[data-clinics-cards-container]');
    if (modeContainer) {
      modeContainer.addEventListener('click', (e) => {
        const button = e.target.closest('[data-clinics-action="select-mode"]');
        if (button) {
          this.#handleModeSelection(e);
        }
      });
    }

    // Back buttons
    const backButtons = document.querySelectorAll('[data-clinics-action="back"]');
    for (const button of backButtons) {
      button.addEventListener('click', () => this.#goBack());
    }
  }

  #handleTypeSelection(e) {
    const button = e.currentTarget;
    
    // Check if login is required
    if (button.dataset.requiresLogin === 'true') {
      this.#redirectToLogin();
      return;
    }
    
    this.selectedType = button.dataset.serviceType;
    
    this.#saveStateToStorage();
    this.#showModeOptions();
    this.#goToStep(2);
  }

  #handleModeSelection(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const button = e.target.closest('[data-clinics-action="select-mode"]');
    
    if (!button) {
      return;
    }
    
    // Check if login is required
    if (button.dataset.requiresLogin === 'true') {
      this.#redirectToLogin();
      return;
    }
    
    this.selectedMode = button.dataset.serviceMode;
    
    // Extract service details
    this.selectedService = {
      mode: this.selectedMode,
      type: this.selectedType,
      servicifyUrl: button.dataset.servicifyUrl
    };

    this.#saveStateToStorage();
    
    // Open Servicify booking window
    this.#loadBooking();
  }

  #showModeOptions() {
    const modeSection = this.steps.modeSelector;
    if (!modeSection) return;

    // Hide all mode cards first
    const allCards = modeSection.querySelectorAll('.clinics-card');
    for (const card of allCards) {
      card.style.display = 'none';
    }

    // Show only cards matching selected type
    const relevantCards = modeSection.querySelectorAll(`[data-service-type="${this.selectedType}"]`);
    for (const card of relevantCards) {
      card.style.display = 'flex';
    }
  }

  #loadBooking() {
    if (!this.selectedService || !this.selectedService.servicifyUrl) {
      return;
    }

    const servicifyUrl = this.selectedService.servicifyUrl;

    // Open Servicify URL - this will trigger the booking calendar
    window.location.href = servicifyUrl;
  }

  #goToStep(stepNumber) {
    this.currentStep = stepNumber;
    this.#showCurrentStep();
    this.#updateProgressIndicator();
    this.#scrollToStep();
  }

  #showCurrentStep() {
    // Hide all steps
    if (this.steps.typeSelector) this.steps.typeSelector.style.display = 'none';
    if (this.steps.modeSelector) this.steps.modeSelector.style.display = 'none';

    // Show current step
    if (this.currentStep === 1 && this.steps.typeSelector) {
      this.steps.typeSelector.style.display = 'block';
    } else if (this.currentStep === 2 && this.steps.modeSelector) {
      this.steps.modeSelector.style.display = 'block';
    }
    // Note: Step 3 (booking) no longer exists - we navigate directly to Servicify
  }

  #updateProgressIndicator() {
    const progressFill = document.querySelector('.clinics-progress__fill');
    const progressCurrent = document.querySelector('.clinics-progress__current');
    
    if (progressFill) {
      const totalSteps = 2; // Only 2 steps now: Type selection → Mode selection → Servicify redirect
      const percentage = Math.min(100, Math.max(0, (this.currentStep / totalSteps) * 100));
      progressFill.style.setProperty('--progress', `${percentage}%`);
    }

    if (progressCurrent) {
      progressCurrent.textContent = `Schritt ${this.currentStep}`;
    }
  }

  #goBack() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.#showCurrentStep();
      this.#updateProgressIndicator();
      this.#scrollToStep();
    }
  }

  #scrollToStep() {
    // Small delay to ensure DOM is updated
    requestAnimationFrame(() => {
      // Get the currently visible wizard step
      const currentStepElement = this.currentStep === 1 
        ? this.steps.typeSelector 
        : this.steps.modeSelector;
      
      if (currentStepElement && currentStepElement.style.display !== 'none') {
        // Get the parent section wrapper (the .section element)
        const sectionWrapper = currentStepElement.closest('.section');
        const targetElement = sectionWrapper || currentStepElement;
        
        // Calculate position with offset for better UX
        const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
        const offset = 80; // 80px offset from top for breathing room
        
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth'
        });
      } else {
        // Fallback: Try to find any wizard element
        const anyWizardElement = document.querySelector('[data-clinics-step="type-selector"], [data-clinics-step="mode-selector"]');
        if (anyWizardElement) {
          const sectionWrapper = anyWizardElement.closest('.section');
          const targetElement = sectionWrapper || anyWizardElement;
          const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
          
        window.scrollTo({
            top: elementPosition - 80,
          behavior: 'smooth'
        });
        }
      }
    });
  }

  #redirectToLogin() {
    // Get login URL from auth state or use default
    const auth = window.NORDLEAF_CLINICS_AUTH || {};
    const loginUrl = auth.loginUrl || '/account/login';
    
    // Save current state before redirect
    this.#saveStateToStorage();
    
    // Build absolute return URL (Shopify requires absolute URLs)
    const returnUrl = window.location.href;
    
    // Shopify uses 'checkout_url' parameter for return redirects
    // Try both for compatibility
    const redirectUrl = `${loginUrl}?checkout_url=${encodeURIComponent(returnUrl)}&return_url=${encodeURIComponent(returnUrl)}`;
    
    // Redirect to login
    window.location.href = redirectUrl;
  }

  #saveStateToStorage() {
    const state = {
      currentStep: this.currentStep,
      selectedType: this.selectedType,
      selectedMode: this.selectedMode,
      selectedService: this.selectedService
    };
    
    try {
      localStorage.setItem('nordleaf-clinics-state', JSON.stringify(state));
    } catch (error) {
      // Silent fail
    }
  }

  #loadStateFromStorage() {
    try {
      const savedState = localStorage.getItem('nordleaf-clinics-state');
      if (savedState) {
        const state = JSON.parse(savedState);
        this.currentStep = state.currentStep || 1;
        this.selectedType = state.selectedType;
        this.selectedMode = state.selectedMode;
        this.selectedService = state.selectedService;

        // Clear saved state immediately
        localStorage.removeItem('nordleaf-clinics-state');

        // If user was selecting a service and got redirected to login, complete the booking now
        if (this.selectedService && this.selectedService.servicifyUrl) {
          this.#loadBooking();
          return; // Exit early, we're navigating away
        }

        // Otherwise, restore UI state based on current step
        if (this.selectedType && this.currentStep >= 2) {
          this.#showModeOptions();
        }
      }
    } catch (error) {
      // Silent fail
    }
  }
}

// Initialize on DOM ready
function initClinicsWizard() {
  new NordleafClinicsWizard();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initClinicsWizard);
} else {
  // DOM already loaded
  initClinicsWizard();
}

